import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import Main from './components/Main';
import ContactPage from './components/ContactPage';
import BlogPage from './components/BlogPage';
import BlogPost from './components/BlogPost';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';
import Products from './components/Products';
import LegalPage from './components/LegalPage';
import StorePage from './components/StorePage';
import StoreProductPage from './components/StoreProductPage';
import CheckoutSuccessPage from './components/CheckoutSuccessPage';
import { clearAdminToken, isAdminTokenExpired, readAdminToken } from './lib/adminAuth';

const privacySections = [
  {
    heading: 'What We Collect',
    body: [
      'We only collect the contact details and project information you choose to send through this site.',
      'We also receive limited technical data such as browser metadata and request logs needed to keep the service secure and available.',
    ],
  },
  {
    heading: 'How We Use It',
    body: [
      'We use submitted information to respond to inquiries, evaluate project fit, and improve the reliability of our services.',
      'We do not sell your personal information or use it for unrelated marketing campaigns.',
    ],
  },
  {
    heading: 'Data Handling',
    body: [
      'Information is retained only as long as it is useful for the conversation, legal compliance, or abuse prevention.',
      'If you need a record corrected or removed, contact Encreage and we can review the request.',
    ],
  },
];

const termsSections = [
  {
    heading: 'Site Use',
    body: [
      'This site is provided for informational and business-contact purposes. You agree not to misuse it, interfere with its operation, or attempt unauthorized access.',
      'Content on the site may change at any time without notice.',
    ],
  },
  {
    heading: 'Project Discussions',
    body: [
      'Messages sent through the contact form do not create a client relationship on their own.',
      'Any formal project work, delivery commitments, or service levels would be governed by a separate written agreement.',
    ],
  },
  {
    heading: 'Liability',
    body: [
      'The site is provided as-is without warranties of uninterrupted availability or fitness for a particular purpose.',
      'To the fullest extent allowed by law, Encreage is not liable for indirect or consequential damages arising from use of the site.',
    ],
  },
];

function RequireAdminSession({ children }) {
  const token = readAdminToken();

  if (isAdminTokenExpired(token)) {
    clearAdminToken();
    return <Navigate to="/admin" replace />;
  }

  return children;
}

export default function App() {
  return (
    <Router>
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow">
          <Routes>
            <Route path="/" element={<Main />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:id" element={<BlogPost />} />
            <Route path="/admin" element={<AdminLogin />} />
            <Route
              path="/admin/dashboard"
              element={
                <RequireAdminSession>
                  <AdminDashboard />
                </RequireAdminSession>
              }
            />
            <Route path="/products" element={<Products />} />
            <Route path="/work" element={<Products />} />
            <Route path="/store" element={<StorePage />} />
            <Route path="/store/:slug" element={<StoreProductPage />} />
            <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
            <Route
              path="/privacy"
              element={
                <LegalPage
                  title="Privacy Policy"
                  intro="This summary explains the basic ways Encreage handles information submitted through the site."
                  sections={privacySections}
                />
              }
            />
            <Route
              path="/terms"
              element={
                <LegalPage
                  title="Terms of Service"
                  intro="These terms cover general use of the public website and early project conversations."
                  sections={termsSections}
                />
              }
            />
            <Route path="*" element={<div className="py-32 text-center text-gray-600">404 - Page not found</div>} />
          </Routes>
        </main>
        <Footer />
      </div>
    </Router>
  );
}
